import React, { useEffect, createContext, useState, useContext, FC } from 'react';
import { Channel } from '@wix/duplexer-js';
import { createDuplexerChannel, eventHelpers } from '@wix/media-manager-sdk';
import { useMMGRSettings } from '../hoc/withMMGRSettings';
import { MainSource } from '../../constants/Source';
import { DuplexerEvent } from '../../constants/DuplexerEvent';
import { TranscodingStatus } from '../../constants/TranscodingStatus';
import { keysToCamelCase } from '../object';
import { useAdapter } from './useAdapter';

export interface IDuplexerMessage {
  type: DuplexerEvent;
  info: any;
  file: { fileName: string; opStatus: TranscodingStatus };
  error: any;
  [key: string]: any;
}

export type TDuplexerHandler = (message: IDuplexerMessage) => void;
export type TDuplexerSubscribers = Partial<Record<DuplexerEvent, TDuplexerHandler>>;

export const __dispatchDuplexerEvent__ = '__dispatchDuplexerEvent__';

const DuplexerContext = createContext<{
  channel: Channel;
}>({
  channel: null,
});

export const DuplexerProvider: FC = ({ children }) => {
  const {
    mmgrSettings: { metaSiteToken },
  } = useMMGRSettings();
  const [channel, setChannel] = useState<Channel>();

  useEffect(() => {
    if (metaSiteToken) {
      const { channel, destroy } = createDuplexerChannel({ metaSiteToken });

      setChannel(channel);

      return destroy;
    };
  }, [metaSiteToken]);

  return <DuplexerContext.Provider value={{ channel }}>{children}</DuplexerContext.Provider>;
};

export const useDuplexer = (subscribers: TDuplexerSubscribers) => {
  const { channel } = useContext(DuplexerContext);
  const privateAdapter = useAdapter(MainSource.SiteFiles);

  const handleMessage = (message: IDuplexerMessage) => {
    const { type, ...data } = eventHelpers.mapper(message, privateAdapter);
    const handler = subscribers[type];
    
    if (handler) {
      handler(keysToCamelCase(data));
    }
  };

  useEffect(() => {
    if (['development', 'test'].includes(process.env.NODE_ENV)) {
      /* There's a lot of consumers of useDuplexer in the app, so need to account
       * for the fact that there could be a function there already,
       * so that each one is 'subscribed' */
      const dispatchDuplexerEvent = window[__dispatchDuplexerEvent__];
      
      window[__dispatchDuplexerEvent__] =
        typeof dispatchDuplexerEvent === 'function'
          ? (message: IDuplexerMessage) => {
              dispatchDuplexerEvent(message);
              handleMessage(message);
            }
          : handleMessage;
    }

    if (!channel) {
      return;
    }

    Object.keys(subscribers).forEach((event) => {
      channel.on(event, handleMessage);
    });

    return () => {
      Object.keys(subscribers).forEach((event) => {
        channel.off(event, handleMessage);
      });
    };
  }, [channel, ...Object.values(subscribers)]);
};
